import React, { useState } from 'react';
import { Check, X, Loader2, Crown, Building2, Sparkles } from 'lucide-react';
import { PLANS, PlanType } from '../../lib/stripe';
import { useAuth } from '../../contexts/AuthContext';
// import pb from '../../lib/pocketbase';

interface PricingPageProps {
  onClose: () => void;
}

export function PricingPage({ onClose }: PricingPageProps) {
  const { user, subscription } = useAuth();
  const [loading, setLoading] = useState<PlanType | null>(null);
  const [error, setError] = useState('');

  const currentPlan = subscription?.plan || 'free';

  const handleSubscribe = async (plan: PlanType) => {
    if (plan === 'free' || plan === currentPlan) return;
    
    setLoading(plan);
    setError('');

    try {
      const priceId = PLANS[plan].priceId;
      
      if (!priceId || !user) {
        throw new Error('Invalid plan or user not logged in');
      }

      // Call PocketBase Stripe hook to create checkout session
      const response = await fetch('https://documents.vhss.org/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          email: user.email,
          priceId: priceId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }

    } catch (err: any) {
      setError(err.message || 'Failed to start checkout');
    } finally {
      setLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!subscription?.stripe_customer_id) {
      setError('No active subscription found');
      return;
    }

    try {
      const response = await fetch('https://documents.vhss.org/api/stripe/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId: subscription.stripe_customer_id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to open customer portal');
      }

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to open customer portal');
    }
  };

  const getPlanIcon = (plan: PlanType) => {
    switch (plan) {
      case 'free': return <Sparkles className="w-6 h-6" />;
      case 'pro': return <Crown className="w-6 h-6" />;
      case 'enterprise': return <Building2 className="w-6 h-6" />;
    }
  };

  const getPlanColor = (plan: PlanType) => {
    switch (plan) {
      case 'free': return 'from-slate-500 to-slate-600';
      case 'pro': return 'from-blue-500 to-blue-600';
      case 'enterprise': return 'from-purple-500 to-purple-600';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
      <div className="bg-slate-900 rounded-xl sm:rounded-2xl max-w-5xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-slate-800 p-4 sm:p-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-white">Choose Your Plan</h2>
            <p className="text-slate-400 mt-1 text-sm sm:text-base">
              {currentPlan === 'free' 
                ? 'Upgrade to unlock all features' 
                : `You're currently on the ${PLANS[currentPlan].name} plan`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors text-2xl"
          >
            ×
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Pricing Cards */}
        <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {(Object.keys(PLANS) as PlanType[]).map((planKey) => {
            const plan = PLANS[planKey];
            const isCurrentPlan = planKey === currentPlan;
            const isPopular = planKey === 'pro';

            return (
              <div
                key={planKey}
                className={`relative rounded-xl border ${
                  isCurrentPlan 
                    ? 'border-green-500 bg-green-500/5' 
                    : isPopular 
                      ? 'border-blue-500 bg-blue-500/5' 
                      : 'border-slate-700 bg-slate-800/50'
                } p-6 flex flex-col`}
              >
                {/* Popular Badge */}
                {isPopular && !isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-500 text-white text-xs font-medium rounded-full">
                    Most Popular
                  </div>
                )}

                {/* Current Plan Badge */}
                {isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                    Current Plan
                  </div>
                )}

                {/* Plan Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${getPlanColor(planKey)} text-white`}>
                    {getPlanIcon(planKey)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
                  </div>
                </div>

                {/* Price */}
                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">
                    ${plan.price.toFixed(2)}
                  </span>
                  {plan.price > 0 && (
                    <span className="text-slate-400 ml-1">/month</span>
                  )}
                </div>

                {/* Features */}
                <div className="flex-1 space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300 text-sm">{feature}</span>
                    </div>
                  ))}
                  {plan.limitations.map((limitation, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <X className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-500 text-sm">{limitation}</span>
                    </div>
                  ))}
                </div>

                {/* Action Button */}
                {isCurrentPlan ? (
                  currentPlan !== 'free' ? (
                    <button
                      onClick={handleManageSubscription}
                      className="w-full py-3 px-4 rounded-lg border border-slate-600 text-slate-300 
                               hover:bg-slate-700 transition-colors text-sm font-medium"
                    >
                      Manage Subscription
                    </button>
                  ) : (
                    <div className="w-full py-3 px-4 rounded-lg bg-slate-700 text-slate-400 
                                  text-center text-sm font-medium">
                      Current Plan
                    </div>
                  )
                ) : (
                  <button
                    onClick={() => handleSubscribe(planKey)}
                    disabled={loading !== null || planKey === 'free'}
                    className={`w-full py-3 px-4 rounded-lg font-medium text-sm transition-all
                              flex items-center justify-center gap-2
                              ${planKey === 'free'
                                ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                                : `bg-gradient-to-r ${getPlanColor(planKey)} text-white hover:opacity-90`
                              }
                              disabled:opacity-50`}
                  >
                    {loading === planKey ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : planKey === 'free' ? (
                      'Downgrade'
                    ) : (
                      `Upgrade to ${plan.name}`
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-800 p-6 text-center text-slate-500 text-sm">
          All plans include a 14-day free trial. Cancel anytime. Prices in USD.
        </div>
      </div>
    </div>
  );
}
